import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

interface NotificationCounts {
  comptes: number;
  commandes: number;
  produits: number;
}

interface NotificationContextType {
  counts: NotificationCounts;
  dismiss: (key: keyof NotificationCounts) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const LS_SEEN_USERS   = 'notif_seen_users';
const LS_SEEN_ORDERS  = 'notif_seen_orders';
const LS_SEEN_STOCKOUT = 'notif_seen_stockout';

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<NotificationCounts>({ comptes: 0, commandes: 0, produits: 0 });

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [usersRes, ordersRes, productsRes] = await Promise.all([
        api.get('users'),
        api.get('commandes'),
        api.get('products/admin'),
      ]);

      // Comptes — nouveaux utilisateurs
      const currentUsers: number = usersRes.data.length;
      const seenUsersStr = localStorage.getItem(LS_SEEN_USERS);
      let newUsers = 0;
      if (seenUsersStr === null) {
        localStorage.setItem(LS_SEEN_USERS, String(currentUsers));
      } else {
        newUsers = Math.max(0, currentUsers - parseInt(seenUsersStr, 10));
      }

      // Commandes — nouvelles commandes
      const currentOrders: number = ordersRes.data.length;
      const seenOrdersStr = localStorage.getItem(LS_SEEN_ORDERS);
      let newOrders = 0;
      if (seenOrdersStr === null) {
        localStorage.setItem(LS_SEEN_ORDERS, String(currentOrders));
      } else {
        newOrders = Math.max(0, currentOrders - parseInt(seenOrdersStr, 10));
      }

      // Produits — hors stock non encore vus
      const outOfStockIds: number[] = (productsRes.data as any[])
        .filter((p: any) => (p.stock ?? 0) === 0)
        .map((p: any) => p.id);
      const seenStockout: number[] = JSON.parse(localStorage.getItem(LS_SEEN_STOCKOUT) || '[]');
      const newStockout = outOfStockIds.filter(id => !seenStockout.includes(id));

      setCounts({ comptes: newUsers, commandes: newOrders, produits: newStockout.length });
    } catch {
      // silencieux
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const dismiss = useCallback(async (key: keyof NotificationCounts) => {
    try {
      if (key === 'comptes') {
        const res = await api.get('users');
        localStorage.setItem(LS_SEEN_USERS, String(res.data.length));
      } else if (key === 'commandes') {
        const res = await api.get('commandes');
        localStorage.setItem(LS_SEEN_ORDERS, String(res.data.length));
      } else if (key === 'produits') {
        const res = await api.get('products/admin');
        const ids = (res.data as any[]).filter((p: any) => (p.stock ?? 0) === 0).map((p: any) => p.id);
        localStorage.setItem(LS_SEEN_STOCKOUT, JSON.stringify(ids));
      }
    } catch {}
    setCounts(prev => ({ ...prev, [key]: 0 }));
  }, []);

  return (
    <NotificationContext.Provider value={{ counts, dismiss }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationProvider');
  return ctx;
};
